import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';

const ContactUs = () => {
  const openLeadForm = () => {
    window.dispatchEvent(new CustomEvent('openLeadForm'));
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Contact Us</h1>
            <p className="text-xl md:text-2xl text-slate-300">
              Get in touch with our experienced legal team. We're here to help you navigate your legal challenges.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Details */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-semibold text-slate-900 mb-8">Get in Touch</h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Whether you need legal advice, want to schedule a consultation, or have questions about our services, 
                our team is ready to assist you. Contact us through any of the methods below.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-700 text-white flex items-center justify-center">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-1">Phone</h3>
                    <p className="text-slate-600">+1 (844) 386-5026</p>
                    <p className="text-sm text-slate-500 mt-1">Monday - Friday, 9:00 AM - 6:00 PM</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-700 text-white flex items-center justify-center">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-1">Email</h3>
                    <p className="text-slate-600">info@tillerkingsleybaleassociates.com</p>
                    <p className="text-sm text-slate-500 mt-1">We typically respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-700 text-white flex items-center justify-center">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-1">Office Address</h3>
                    <p className="text-slate-600">
                      44 Tehama St<br />
                      San Francisco, CA 94105
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-700 text-white flex items-center justify-center">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-1">Office Address</h3>
                    <p className="text-slate-600">
                      39 Roehampton Ave<br />
                      Toronto, ON M4P 0B9, Canada
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-700 text-white flex items-center justify-center">
                    <Clock className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-1">Business Hours</h3>
                    <p className="text-slate-600">
                      Monday - Friday: 9:00 AM - 6:00 PM<br />
                      Saturday: 10:00 AM - 2:00 PM<br />
                      Sunday: Closed<br />
                      <span className="text-sm text-amber-700 font-medium">Emergency consultations available 24/7</span>
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex flex-col gap-8"
            >
              <img
                src="https://images.unsplash.com/photo-1631509057887-7ef19853bc39?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Law office building"
                className="w-full h-[400px] object-cover shadow-xl"
              />
              <div className="bg-slate-50 p-8 border-l-4 border-amber-700">
                <h3 className="text-2xl font-semibold text-slate-900 mb-4">Schedule a Consultation</h3>
                <p className="text-slate-600 mb-6">
                  Ready to discuss your legal needs? Click below to schedule a consultation with one of our experienced attorneys.
                </p>
                <button
                  onClick={openLeadForm}
                  className="bg-slate-900 text-white hover:bg-slate-800 px-8 py-4 font-serif text-lg transition-all hover:tracking-wider shadow-lg hover:shadow-xl"
                  data-testid="contact-cta-button"
                >
                  Request Consultation
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Directions/Map Section - Hidden */}
      <section className="hidden py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-semibold text-slate-900 mb-4">Visit Our Office</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our office is conveniently located in the heart of downtown, easily accessible by public transportation and with parking available.
            </p>
          </div>

          <div className="bg-white p-8 shadow-sm border border-slate-100">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Public Transit</h3>
                <p className="text-slate-600">
                  Subway: Lines 1, 2, 3 to Downtown Station<br />
                  Bus: Routes 15, 22, 45
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Parking</h3>
                <p className="text-slate-600">
                  Validated parking available<br />
                  Underground garage entrance on Main St
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Accessibility</h3>
                <p className="text-slate-600">
                  Wheelchair accessible<br />
                  Elevator to all floors
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Have Questions?</h2>
          <p className="text-xl text-slate-300 mb-8">
            Our legal team is ready to provide the guidance you need. Contact us today to get started.
          </p>
          <button
            onClick={openLeadForm}
            className="bg-amber-700 text-white hover:bg-amber-800 px-8 py-4 font-serif text-lg transition-colors shadow-lg hover:shadow-xl"
            data-testid="contact-bottom-cta"
          >
            Get Legal Help
          </button>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
